import React, { useState, useMemo, useRef } from "react";
import cx from "classnames";
import Cleave from "cleave.js/react";
import { debounce } from "lodash";
import "./Input.scss";
import "cleave.js/dist/addons/cleave-phone.us";

// Type assertion for Cleave component to avoid TypeScript issues with latest version of React
// Cleave.js is unmaintained :( TODO: migrate to a maintained library
const CleaveComponent = Cleave as any;

type InputProps = {
  placeholder?: string;
  value?: string | number;
  name: string;
  onChange?: (name: string, value: any) => void;
  onBlur?: (name: string, value: any) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  label?: string;
  error?: boolean;
  disabled?: boolean;
  options?: Record<string, any>; // Cleave.js options
  debounceMs?: number;
  autoFocus?: boolean;
  required?: boolean;
  useDebounce?: boolean;
  type?: React.HTMLInputTypeAttribute;
  step?: string | number;
};

const DEBOUNCE_MS = 300;

const Input: React.FC<InputProps> = ({
  placeholder = "",
  value = "",
  name,
  onChange,
  onBlur,
  onKeyDown,
  prefix,
  suffix,
  className,
  style,
  label,
  error = false,
  disabled = false,
  options = {},
  debounceMs = DEBOUNCE_MS,
  autoFocus = false,
  required = false,
  useDebounce = true,
  type = "text",
  step,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const cleaveRef = useRef<HTMLInputElement>(null);

  const debouncedOnChange = useMemo(
    () =>
      onChange && debounceMs > 0
        ? debounce((name: string, value: any) => {
            onChange(name, value);
          }, debounceMs)
        : undefined,
    [onChange, debounceMs],
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onChange(name, e.target.value);
    }
  };

  const handleCleaveChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (useDebounce && debouncedOnChange) {
      debouncedOnChange(name, e.target.value);
    } else {
      handleChange(e);
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    if (onBlur) {
      onBlur(name, e.target.value);
    }
  };

  const handleContainerClick = () => {
    if (!disabled && cleaveRef.current) {
      cleaveRef.current.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (onKeyDown) {
      onKeyDown(e);
    }
  };

  // Function to set the ref for Cleave
  const setInputRef = (element: HTMLInputElement) => {
    cleaveRef.current = element;
  };

  // Check if we actually need Cleave.js formatting
  const needsCleave = Object.keys(options).length > 0;

  const inputClasses = cx("Input", className, {
    "Input--Focused": isFocused,
    "Input--Filled": value !== "",
    "Input--Error": error,
    "Input--Disabled": disabled,
    "Input--WithLabel": !!label,
  });

  const fieldClasses = cx("Input__Field", {
    "Input__Field--Filled": value !== placeholder,
  });

  return (
    <div className="Input__Container">
      {label && (
        <label htmlFor={name} className="Input__Label">
          {label} {required && <span className="Input__Required">*</span>}
        </label>
      )}

      <div
        className={inputClasses}
        style={style}
        onClick={handleContainerClick}
      >
        {prefix && <div className="Input__Prefix">{prefix}</div>}

        {needsCleave ? (
          <CleaveComponent
            htmlRef={setInputRef}
            className={fieldClasses}
            name={name}
            id={name}
            placeholder={placeholder}
            value={value}
            options={options}
            onChange={handleCleaveChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            autoFocus={autoFocus}
          />
        ) : (
          <input
            ref={cleaveRef}
            className={fieldClasses}
            name={name}
            id={name}
            placeholder={placeholder}
            value={value}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            disabled={disabled}
            autoFocus={autoFocus}
            type={type}
            step={step}
            onKeyDown={handleKeyDown}
          />
        )}

        {suffix && <div className="Input__Suffix">{suffix}</div>}
      </div>
    </div>
  );
};

export { Input };
